/**
 * LockedFeatureDialog — iter1090 — Голям модал за заключени функции.
 *
 * Показва:
 *   • Общ процент попълненост на профила (progress bar).
 *   • Списък с всички секции и статуса им (готова/липсваща).
 *   • Кое се отключва с всяка секция.
 *   • Ясен CTA към точната секция, която липсва за функцията.
 *
 * Използва се от TwinNetworkPage за locked twins и от други заключени
 * функции. Recyclable — reads профилa само когато е отворен.
 */
import React from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { computeCompleteness, PROFILE_SECTIONS } from "@/lib/profileCompleteness";
import { ArrowRight, CheckCircle2, Circle, Lock, Sparkles } from "lucide-react";

/**
 * @param {boolean} open
 * @param {(v:boolean)=>void} onOpenChange
 * @param {string} featureName — human-friendly title of the locked feature
 * @param {string} [requiredSectionKey] — key от PROFILE_SECTIONS; ако липсва
 *   диалогът избира първата непопълнена секция.
 */
export default function LockedFeatureDialog({
  open,
  onOpenChange,
  featureName,
  requiredSectionKey,
}) {
  const { user, API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const [profileRes, twinRes] = await Promise.all([
          axios.get(`${API}/auth/me`).catch(() => ({ data: user })),
          axios.get(`${API}/health-twin/twin-score`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        const p = profileRes.data?.profile || {};
        // iter1092 — coverage_pct + правилен nested profile.
        const twin_readiness_pct = twinRes.data?.coverage_pct || 0;
        // iter1092 — Broi от profile.labValues (real shape) — /lab-snapshots 404.
        const lab_snapshots_count =
          (Array.isArray(p.labValues) ? p.labValues.length : 0)
          || (Array.isArray(p.labs) ? p.labs.length : 0);
        setData({
          ...p,
          twin_readiness_pct,
          lab_snapshots_count,
        });
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open, user, API]);

  const sex = data?.personal?.gender || data?.gender || data?.sex;
  const { pct, sections } = data ? computeCompleteness(data, sex) : { pct: 0, sections: PROFILE_SECTIONS.map(s => ({ ...s, applicable: true, complete: false })) };

  // Приоритетна секция: тази, която е поискана, или първата непопълнена.
  const required = requiredSectionKey
    ? sections.find((s) => s.key === requiredSectionKey && s.applicable)
    : null;
  const next = required && !required.complete
    ? required
    : sections.find((s) => s.applicable && !s.complete);

  const applicable = sections.filter((s) => s.applicable);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="locked-feature-dialog"
        className={`max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto rounded-2xl ${
          isDark ? "bg-[hsl(222,47%,10%)] border-[hsl(223,47%,18%)] text-slate-100" : "bg-white"
        }`}
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Lock className={`w-5 h-5 ${isDark ? "text-amber-300" : "text-amber-600"}`} />
            <DialogTitle data-testid="locked-feature-title" className="text-base sm:text-lg font-bold">
              {bg ? "Още не е отключено" : "Not unlocked yet"}
            </DialogTitle>
          </div>
          <DialogDescription className={`text-sm mt-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            {featureName
              ? (bg
                ? `За да работи „${featureName}“, попълнете следната секция от профила Ви.`
                : `To use "${featureName}", complete the profile section below.`)
              : (bg
                ? "Тази функция изисква още малко данни от профила Ви."
                : "This feature needs a bit more profile data.")
            }
          </DialogDescription>
        </DialogHeader>

        {/* Progress bar */}
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1.5">
            <span className={`text-xs font-semibold uppercase tracking-wide ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              {bg ? "Профилът Ви" : "Your profile"}
            </span>
            <span
              data-testid="locked-feature-pct"
              className={`text-xs font-bold ${isDark ? "text-slate-200" : "text-slate-800"}`}
            >
              {pct}%
            </span>
          </div>
          <div className={`h-2 rounded-full overflow-hidden ${isDark ? "bg-white/10" : "bg-slate-100"}`}>
            <div
              data-testid="locked-feature-progress"
              className="h-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Highlighted next section */}
        {next && (
          <div
            data-testid="locked-feature-next-section"
            className={`mt-4 rounded-xl border-2 p-3.5 ${
              isDark
                ? "bg-purple-500/10 border-purple-500/40"
                : "bg-purple-50 border-purple-300"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className={`w-4 h-4 ${isDark ? "text-purple-300" : "text-purple-600"}`} />
              <span className={`text-xs font-bold uppercase tracking-wide ${isDark ? "text-purple-300" : "text-purple-700"}`}>
                {bg ? "Попълнете сега" : "Fill in now"}
              </span>
            </div>
            <div className={`text-sm font-bold mb-1 ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? next.label_bg : next.label_en}
            </div>
            {next.unlocks_bg?.length ? (
              <ul className={`text-xs mt-1.5 space-y-0.5 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                {next.unlocks_bg.map((u) => (
                  <li key={u} data-testid={`locked-dialog-unlock-${u.replace(/\s+/g, "-")}`}>• {u}</li>
                ))}
              </ul>
            ) : null}
            <Link
              to={next.action_route}
              onClick={() => onOpenChange(false)}
              data-testid="locked-feature-cta"
              className={`mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold ${
                isDark ? "bg-purple-500 hover:bg-purple-400 text-white" : "bg-purple-600 hover:bg-purple-700 text-white"
              }`}
            >
              {bg ? "Отвори секцията" : "Open section"} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}

        {/* All sections list */}
        <div className="mt-4">
          <div className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {bg ? "Пълен списък" : "All sections"}
          </div>
          <ul className="space-y-1.5">
            {applicable.map((s) => {
              const isNext = next && s.key === next.key;
              return (
                <li
                  key={s.key}
                  data-testid={`locked-dialog-section-${s.key}`}
                  className={`flex items-start gap-2 p-2 rounded-lg ${
                    s.complete
                      ? (isDark ? "bg-emerald-500/10" : "bg-emerald-50")
                      : isNext
                      ? (isDark ? "bg-purple-500/10" : "bg-purple-50")
                      : (isDark ? "bg-white/5" : "bg-slate-50")
                  }`}
                >
                  {s.complete
                    ? <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
                    : <Circle className={`w-4 h-4 mt-0.5 shrink-0 ${isDark ? "text-slate-500" : "text-slate-400"}`} />
                  }
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-semibold ${
                      s.complete
                        ? (isDark ? "text-emerald-300" : "text-emerald-700")
                        : (isDark ? "text-slate-200" : "text-slate-800")
                    }`}>
                      {bg ? s.label_bg : s.label_en}
                    </div>
                    {!s.complete && s.unlocks_bg?.length ? (
                      <div className={`text-[11px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                        {bg ? "Отключва: " : "Unlocks: "}{s.unlocks_bg.join(", ")}
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
